// istanbul ignore file
import React from 'react'
import { Container, Grid } from '@mui/material'
import StageForm from './StageForm'
import { type Stage } from './types'
import { type Option } from '../../../../components/GenXDropdown'
import { useDispatch, useSelector } from 'react-redux'
import { setStages } from '../../redux/customKpiSlice'
import { type RootState } from '../../../../store'
import { type Asset } from '../../services/assetListService'

interface LabelValue {
  label: string
  value: string
  typeId?: string
}

interface CreateKpiTabProps {
  assetList: Asset[]
  kpiType: string
  selectedAsset: LabelValue | null
  deviceTypeId?: string
}

const CreateKpiTab: React.FC<CreateKpiTabProps> = (props) => {
  const kpiStages = useSelector((state: RootState) => state.customKpi)
  const dispatch = useDispatch()
  const [stages, setStageState] = React.useState(kpiStages.stages)

  const operators: Option[] = [
    { value: 'ARITHMETIC', label: 'Arithmetic' },
    { value: 'LOGICAL', label: 'Logical' }
  ]

  const logicalIndicators: Option[] = [
    { value: 'AND', label: '&' },
    { value: 'OR', label: '|' },
    { value: 'LESS_THAN', label: '<' },
    { value: 'GREATER_THAN', label: '>' },
    { value: 'EQUAL', label: '=' },
    { value: 'NOT_EQUAL', label: '!=' },
    { value: 'LESS_OR_EQUAL', label: '<=' },
    { value: 'GREATER_OR_EQUAL', label: '>=' }
  ]
  const arithmeticIndicators: Option[] = [
    { value: 'ADD', label: '+' },
    { value: 'SUBTRACT', label: '-' },
    { value: 'MULTIPLY', label: '*' },
    { value: 'DIVIDE', label: '/' },
    { value: 'POWER', label: '^' },
    { value: 'MIN', label: 'min' },
    { value: 'MAX', label: 'max' },
    { value: 'AVG', label: 'avg' }
  ]

  // Transform assetList
  const devices = props.assetList.map((device: any) => ({
    id: device.id,
    name: device.name,
    typeId: device.deviceTypeId
  }))
  const devicesArr = (props.selectedAsset != null) ? [{ id: props.selectedAsset.value, name: props.selectedAsset.label, typeId: props.selectedAsset.typeId }, ...devices] : devices

  React.useEffect(() => {
    setStageState(kpiStages.stages)
  }, [kpiStages.stages])

  // Function to handle changes in stage fields
  /* istanbul ignore next */
  const handleStageChange = (stageIndex: number, field: keyof Stage, value: any, asset?: Option | null, device?: Option | null, constantValue?: string): void => {
    try {
      if (field === 'parameter1' || field === 'parameter2') {
        const parameter = {
          asset: asset?.value ?? '',
          device: device?.label ?? '',
          deviceId: device?.value ?? '',
          parameterId: value.value,
          parameter: value.label,
          parameterType: value.type,
          constantValue: constantValue ?? ''
        }
        value = parameter
      }
      setStageState((prevStages) => {
        const updatedStages = prevStages.map((stage, index) =>
          index === stageIndex ? { ...stage, [field]: value } : stage
        )
        dispatch(setStages(updatedStages))
        return updatedStages
      })
    } catch (error) {
      console.error('Error occurred while updating stage', error)
    }
  }

  return (
    <Grid sx={{ width: '90vw', maxHeight: '80vh' }} data-testid='create-kpi-tab'>
      <Container maxWidth={false} sx={{ display: 'flex', paddingLeft: '16px !important', flexDirection: 'row', overflowX: 'auto', '&::-webkit-scrollbar': { height: '12px' }, '&::-webkit-scrollbar-thumb': { background: '#FFFFFF80' } }}>
        {kpiStages.stages.map((stage, index) => (
          <StageForm
            key={index}
            data-testid={`stage-form-${index}`}
            index={index}
            stages={stages}
            isTotalizer={kpiStages.totalizer}
            assetList={devicesArr}
            arithmeticIndicators={arithmeticIndicators}
            logicalIndicators={logicalIndicators}
            kpiType={props.kpiType}
            deviceTypeId={props.deviceTypeId}
            operators={operators}
            handleStageChange={handleStageChange}
            selectedAssetId={props.selectedAsset != null ? props.selectedAsset.value : ''}
          />
        ))}
      </Container>
    </Grid>
  )
}

export default CreateKpiTab
